import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';
import type { VendorCategory, PriceRange } from '@/types/vendor';

export interface VendorProfile {
  id: string;
  user_id: string;
  name: string;
  slug: string;
  category: string;
  description: string | null;
  short_description: string | null;
  image: string | null;
  location: string | null;
  price_range: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  website: string | null;
  is_approved: boolean;
  is_featured: boolean;
  created_at: string;
  updated_at: string;
}

// Public vendor type for directory display
export interface PublicVendor {
  id: string;
  name: string;
  slug: string;
  category: VendorCategory;
  description: string | null;
  shortDescription: string | null;
  image: string | null;
  location: string | null;
  priceRange: PriceRange;
  contactEmail: string | null;
  contactPhone: string | null;
  website: string | null;
  isFeatured: boolean;
  // Computed fields from related tables
  gallery: string[];
  services: string[];
  rating: number;
  reviewCount: number;
}

export interface VendorService {
  id: string;
  vendor_id: string;
  name: string;
  description: string | null;
  price: number | null;
  price_type: string;
  image: string | null;
  is_featured: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface VendorInquiry {
  id: string;
  vendor_id: string;
  couple_user_id: string | null;
  couple_name: string;
  couple_email: string;
  wedding_date: string | null;
  message: string;
  status: string;
  vendor_reply: string | null;
  replied_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface VendorStats {
  id: string;
  vendor_id: string;
  date: string;
  views: number;
  inquiries: number;
  favorites: number;
}

// Helper to transform DB vendor to public format
const transformVendorToPublic = (
  vendor: VendorProfile,
  gallery: { image_url: string }[] = [],
  services: { name: string }[] = []
): PublicVendor => ({
  id: vendor.id,
  name: vendor.name,
  slug: vendor.slug,
  category: vendor.category as VendorCategory,
  description: vendor.description,
  shortDescription: vendor.short_description,
  image: vendor.image,
  location: vendor.location,
  priceRange: (vendor.price_range || 'moderate') as PriceRange,
  contactEmail: vendor.contact_email,
  contactPhone: vendor.contact_phone,
  website: vendor.website,
  isFeatured: vendor.is_featured ?? false,
  gallery: gallery.map(g => g.image_url),
  services: services.map(s => s.name),
  // TODO: Implement reviews table for real ratings
  rating: 4.5 + Math.random() * 0.5, // Placeholder
  reviewCount: Math.floor(Math.random() * 200) + 50, // Placeholder
});

// ===========================================
// PUBLIC HOOKS (for vendor directory)
// ===========================================

// Fetch all approved vendors (public directory)
export const useVendors = (category?: VendorCategory | null) => {
  return useQuery({
    queryKey: ['vendors', category],
    queryFn: async () => {
      let query = supabase
        .from('vendors')
        .select('*')
        .eq('is_approved', true)
        .order('is_featured', { ascending: false })
        .order('name');

      if (category) {
        query = query.eq('category', category);
      }

      const { data: vendors, error } = await query;
      if (error) throw error;

      // For each vendor, fetch gallery and services
      const publicVendors = await Promise.all(
        (vendors || []).map(async (vendor) => {
          const [galleryRes, servicesRes] = await Promise.all([
            supabase
              .from('vendor_gallery')
              .select('image_url')
              .eq('vendor_id', vendor.id)
              .order('display_order'),
            supabase
              .from('vendor_services')
              .select('name')
              .eq('vendor_id', vendor.id)
              .order('display_order'),
          ]);

          return transformVendorToPublic(
            vendor as VendorProfile,
            galleryRes.data || [],
            servicesRes.data || []
          );
        })
      );

      return publicVendors;
    },
  });
};

// Fetch featured vendors (for homepage)
export const useFeaturedVendors = (limit: number = 6) => {
  return useQuery({
    queryKey: ['vendors', 'featured', limit],
    queryFn: async () => {
      const { data: vendors, error } = await supabase
        .from('vendors')
        .select('*')
        .eq('is_approved', true)
        .eq('is_featured', true)
        .order('name')
        .limit(limit);

      if (error) throw error;

      const publicVendors = await Promise.all(
        (vendors || []).map(async (vendor) => {
          const [galleryRes, servicesRes] = await Promise.all([
            supabase
              .from('vendor_gallery')
              .select('image_url')
              .eq('vendor_id', vendor.id)
              .order('display_order'),
            supabase
              .from('vendor_services')
              .select('name')
              .eq('vendor_id', vendor.id)
              .order('display_order'),
          ]);

          return transformVendorToPublic(
            vendor as VendorProfile,
            galleryRes.data || [],
            servicesRes.data || []
          );
        })
      );

      return publicVendors;
    },
  });
};

// Fetch single vendor by slug (public detail page)
export const useVendorBySlug = (slug?: string) => {
  return useQuery({
    queryKey: ['vendor', slug],
    queryFn: async () => {
      if (!slug) return null;

      const { data: vendor, error } = await supabase
        .from('vendors')
        .select('*')
        .eq('slug', slug)
        .eq('is_approved', true)
        .maybeSingle();

      if (error) throw error;
      if (!vendor) return null;

      // Track view stat
      try {
        await supabase.rpc('increment_vendor_stat', {
          p_vendor_id: vendor.id,
          p_stat_type: 'view',
        });
      } catch {
        // Silent fail for stats
      }

      const [galleryRes, servicesRes] = await Promise.all([
        supabase
          .from('vendor_gallery')
          .select('image_url')
          .eq('vendor_id', vendor.id)
          .order('display_order'),
        supabase
          .from('vendor_services')
          .select('name')
          .eq('vendor_id', vendor.id)
          .order('display_order'),
      ]);

      return transformVendorToPublic(
        vendor as VendorProfile,
        galleryRes.data || [],
        servicesRes.data || []
      );
    },
    enabled: !!slug,
  });
};

// Create inquiry for a vendor
export const useCreateInquiry = () => {
  return useMutation({
    mutationFn: async (inquiry: {
      vendor_id: string;
      couple_name: string;
      couple_email: string;
      wedding_date?: string | null;
      message: string;
    }) => {
      const { data, error } = await supabase
        .from('vendor_inquiries')
        .insert({
          ...inquiry,
          status: 'pending',
        })
        .select()
        .single();

      if (error) throw error;

      // Track inquiry stat
      try {
        await supabase.rpc('increment_vendor_stat', {
          p_vendor_id: inquiry.vendor_id,
          p_stat_type: 'inquiry',
        });
      } catch {
        // Silent fail for stats
      }

      return data;
    },
    onSuccess: () => {
      toast.success('Solicitud enviada correctamente');
    },
    onError: (error) => {
      toast.error('Error al enviar solicitud: ' + error.message);
    },
  });
};

// ===========================================
// VENDOR BACKOFFICE HOOKS (authenticated)
// ===========================================

// Fetch vendor profile for current user
export const useVendorProfile = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['vendor-profile', user?.id],
    queryFn: async () => {
      if (!user) return null;

      const { data, error } = await supabase
        .from('vendors')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      return data as VendorProfile | null;
    },
    enabled: !!user,
  });
};

// Update vendor profile
export const useUpdateVendorProfile = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (updates: Partial<VendorProfile>) => {
      if (!user) throw new Error('No user');

      const { data, error } = await supabase
        .from('vendors')
        .update(updates)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-profile'] });
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      toast.success('Perfil actualizado');
    },
    onError: (error) => {
      toast.error('Error al actualizar: ' + error.message);
    },
  });
};

// Fetch vendor services
export const useVendorServices = (vendorId?: string) => {
  return useQuery({
    queryKey: ['vendor-services', vendorId],
    queryFn: async () => {
      if (!vendorId) return [];

      const { data, error } = await supabase
        .from('vendor_services')
        .select('*')
        .eq('vendor_id', vendorId)
        .order('display_order');

      if (error) throw error;
      return data as VendorService[];
    },
    enabled: !!vendorId,
  });
};

// Create vendor service
export const useCreateVendorService = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (service: Omit<VendorService, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('vendor_services')
        .insert(service)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['vendor-services', data.vendor_id] });
      toast.success('Servicio creado');
    },
    onError: (error) => {
      toast.error('Error al crear servicio: ' + error.message);
    },
  });
};

// Update vendor service
export const useUpdateVendorService = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<VendorService> & { id: string }) => {
      const { data, error } = await supabase
        .from('vendor_services')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['vendor-services', data.vendor_id] });
      toast.success('Servicio actualizado');
    },
    onError: (error) => {
      toast.error('Error al actualizar: ' + error.message);
    },
  });
};

// Delete vendor service
export const useDeleteVendorService = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, vendorId }: { id: string; vendorId: string }) => {
      const { error } = await supabase
        .from('vendor_services')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return { id, vendorId };
    },
    onSuccess: ({ vendorId }) => {
      queryClient.invalidateQueries({ queryKey: ['vendor-services', vendorId] });
      toast.success('Servicio eliminado');
    },
    onError: (error) => {
      toast.error('Error al eliminar: ' + error.message);
    },
  });
};

// Fetch vendor inquiries
export const useVendorInquiries = (vendorId?: string) => {
  return useQuery({
    queryKey: ['vendor-inquiries', vendorId],
    queryFn: async () => {
      if (!vendorId) return [];

      const { data, error } = await supabase
        .from('vendor_inquiries')
        .select('*')
        .eq('vendor_id', vendorId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as VendorInquiry[];
    },
    enabled: !!vendorId,
  });
};

// Reply to inquiry
export const useReplyToInquiry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, reply, status }: { id: string; reply: string; status: string }) => {
      const { data, error } = await supabase
        .from('vendor_inquiries')
        .update({
          vendor_reply: reply,
          status,
          replied_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['vendor-inquiries', data.vendor_id] });
      toast.success('Respuesta enviada');
    },
    onError: (error) => {
      toast.error('Error al responder: ' + error.message);
    },
  });
};

// Fetch vendor stats
export const useVendorStats = (vendorId?: string, days: number = 30) => {
  return useQuery({
    queryKey: ['vendor-stats', vendorId, days],
    queryFn: async () => {
      if (!vendorId) return { stats: [], totals: { views: 0, inquiries: 0, favorites: 0 } };

      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const { data, error } = await supabase
        .from('vendor_stats')
        .select('*')
        .eq('vendor_id', vendorId)
        .gte('date', startDate.toISOString().split('T')[0])
        .order('date');

      if (error) throw error;

      const stats = data as VendorStats[];
      const totals = stats.reduce(
        (acc, stat) => ({
          views: acc.views + (stat.views || 0),
          inquiries: acc.inquiries + (stat.inquiries || 0),
          favorites: acc.favorites + (stat.favorites || 0),
        }),
        { views: 0, inquiries: 0, favorites: 0 }
      );

      return { stats, totals };
    },
    enabled: !!vendorId,
  });
};

// Check if user is a vendor
export const useIsVendor = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['is-vendor', user?.id],
    queryFn: async () => {
      if (!user) return false;

      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'vendor')
        .maybeSingle();

      if (error) return false;
      return !!data;
    },
    enabled: !!user,
  });
};
